/* W7.5B — Deal System Home : cockpit portefeuille.
 * KPI 100% réels servis par GET /workspace/deals/cockpit — aucun chiffre décoratif. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Handshake, Wallet, AlertTriangle, Scale, Loader2, Zap, Clock,
  ChevronRight, ArrowUpRight, Layers, Newspaper, Scale3d, History,
} from "lucide-react";
import { FirstDecisionPath } from "@/components/FirstDecisionPath";
import { ActionDigest, EVENT_LABELS } from "@/components/ActionDigest";

const fmtCHF = (v) =>
  new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};
const PHASE_LABELS = { discover: "Discover", qualify: "Qualify", structure: "Structure", execute: "Execute", close: "Close" };
const PHASE_COLORS = { discover: "#378ADD", qualify: "#7F77DD", structure: "#EFD127", execute: "#EF9F27", close: "#1D9E75" };

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="noam-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: accent || "#378ADD" }} /> {label}
      </div>
      <div className="noam-display text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

export default function DealSystemHome() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);

  useEffect(() => {
    api.get("/workspace/deals/cockpit")
      .then((r) => setData(r.data))
      .catch((e) => { setError(fmtError(e)); notify.error(fmtError(e)); });
  }, []);

  if (error) return <div className="py-20 text-center text-sm text-zinc-500" data-testid="cockpit-error">{error}</div>;
  if (!data) return <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const { stats, phases, distribution, attention, negotiations, recent_activity, next_best_actions,
    open_decisions = [], stale_deals = [], diverged_convictions = [] } = data;
  const maxDist = Math.max(1, ...phases.map((p) => distribution[p] || 0));

  return (
    <div className="space-y-5" data-testid="deal-cockpit-root">
      {digestOpen && <ActionDigest onClose={() => setDigestOpen(false)} />}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="noam-overline">Deal System</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">Deal</h1>
          <p className="text-xs text-zinc-500 mt-1">Ce qui a changé, ce qui exige votre attention, la prochaine meilleure action.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setDigestOpen(true)} className="btn-ghost text-xs border border-white/10" data-testid="cockpit-open-digest">
            <Newspaper className="w-3.5 h-3.5" /> Digest du jour
          </button>
          <Link to="/app/deal-pipeline" className="btn-primary text-xs" data-testid="cockpit-open-pipeline">
            <Layers className="w-3.5 h-3.5" /> Ouvrir le pipeline
          </Link>
        </div>
      </div>

      <FirstDecisionPath />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Handshake} label="Deals actifs" value={stats.active_count} testid="cockpit-stat-active" />
        <Stat icon={Wallet} label="Valeur active" value={fmtCHF(stats.active_value_chf)} testid="cockpit-stat-value" />
        <Stat icon={AlertTriangle} label="Nécessitent attention" value={stats.attention_count} accent="#EF9F27" testid="cockpit-stat-attention" />
        <Stat icon={Scale} label="Négociations" value={stats.negotiations_active} accent="#1D9E75" testid="cockpit-stat-negos" />
      </div>

      <div className="noam-card p-4" data-testid="cockpit-distribution">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Répartition par phase</div>
        <div className="grid grid-cols-5 gap-2">
          {phases.map((p) => (
            <Link key={p} to={`/app/deal-pipeline#${p}`} className="group text-center" data-testid={`cockpit-dist-${p}`}>
              <div className="h-16 flex items-end justify-center mb-1.5">
                <div className="w-full max-w-[56px] rounded-t transition-all group-hover:opacity-80"
                  style={{ height: `${Math.max(6, ((distribution[p] || 0) / maxDist) * 100)}%`, background: PHASE_COLORS[p] }} />
              </div>
              <div className="text-[10px] text-zinc-500">{PHASE_LABELS[p]}</div>
              <div className="text-sm font-bold font-mono">{distribution[p] || 0}</div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="noam-card p-4" data-testid="cockpit-attention">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[#EF9F27]" /> Deals nécessitant attention
          </div>
          {attention.length === 0 && <div className="text-xs text-zinc-600 py-4 text-center">Aucun deal en alerte.</div>}
          <div className="space-y-1.5">
            {attention.map((a) => (
              <Link key={a.deal_id} to={`/app/deals/${a.deal_id}`}
                className="flex items-start gap-2 p-2 rounded bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-xs"
                data-testid={`cockpit-attention-${a.deal_id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#EF9F27] mt-1.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-zinc-200 truncate">{a.title}</div>
                  <div className="text-[10px] text-zinc-500">{a.reasons.join(" · ")}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="noam-card p-4" data-testid="cockpit-actions">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
            <Zap className="w-3.5 h-3.5 text-[#EFD127]" /> Next best actions
          </div>
          {next_best_actions.length === 0 && <div className="text-xs text-zinc-600 py-4 text-center">Rien à signaler.</div>}
          <div className="space-y-1.5">
            {next_best_actions.map((a, i) => (
              <Link key={i} to={`/app/deals/${a.deal_id}`}
                className="flex items-start gap-2 p-2 rounded bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-xs"
                data-testid={`cockpit-action-${i}`}>
                <ChevronRight className="w-3.5 h-3.5 text-[#EFD127] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-zinc-200">{a.label}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{a.deal_title} — {a.reason}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="noam-card p-4" data-testid="cockpit-open-decisions">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
            <Scale3d className="w-3.5 h-3.5 text-[#7F77DD]" /> Décisions ouvertes
          </div>
          {open_decisions.length === 0 && <div className="text-xs text-zinc-600 py-4 text-center">Aucune décision en attente de verdict.</div>}
          <div className="space-y-1.5">
            {open_decisions.map((d) => (
              <Link key={d.id} to={d.deal_id ? `/app/deals/${d.deal_id}` : "/app/decisions"}
                className="flex items-start gap-2 p-2 rounded bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-xs"
                data-testid={`cockpit-open-decision-${d.id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#7F77DD] mt-1.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 truncate">{d.title}</div>
                  <div className="text-[10px] text-zinc-500">
                    Ouverte depuis {d.days_open ?? 0} j
                    {(d.economics || {}).adjusted_ev_chf != null && ` · AdjEV ${fmtCHF(d.economics.adjusted_ev_chf)}`}
                    {d.recommendation && ` · Noam suggère ${d.recommendation}`}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {diverged_convictions.length > 0 && (
            <div className="mt-3 space-y-1" data-testid="cockpit-diverged">
              <div className="text-[10px] uppercase tracking-widest text-[#EF9F27]">Convictions divergentes depuis le verdict</div>
              {diverged_convictions.map((d) => (
                <div key={d.decision_id} className="text-[10px] text-zinc-400 p-1.5 rounded bg-[#EF9F27]/5 border border-[#EF9F27]/20">
                  {d.title} — AdjEV à la décision {fmtCHF(d.adjev_at_decision_chf)} → aujourd'hui {fmtCHF(d.adjev_current_chf)} (v{d.version})
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="noam-card p-4" data-testid="cockpit-stale">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
            <History className="w-3.5 h-3.5 text-[#EF9F27]" /> Deals stagnants
          </div>
          {stale_deals.length === 0 && <div className="text-xs text-zinc-600 py-4 text-center">Aucun deal ne stagne (≥14 j sans mise à jour).</div>}
          <div className="space-y-1.5">
            {stale_deals.map((s) => (
              <Link key={s.deal_id} to={`/app/deals/${s.deal_id}`}
                className="flex items-start gap-2 p-2 rounded bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-xs"
                data-testid={`cockpit-stale-${s.deal_id}`}>
                <Clock className="w-3.5 h-3.5 text-[#EF9F27] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-zinc-200 truncate">{s.title}</div>
                  <div className="text-[10px] text-zinc-500">{s.why}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="noam-card p-4" data-testid="cockpit-activity">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
            <Clock className="w-3.5 h-3.5 text-[#378ADD]" /> Activité récente
          </div>
          {recent_activity.length === 0 && (
            <div className="text-xs text-zinc-600 py-4 text-center">Aucun événement journalisé pour l'instant — les transitions de phase apparaîtront ici.</div>
          )}
          <div className="space-y-1.5">
            {recent_activity.map((e) => (
              <Link key={e.id} to={`/app/deals/${e.deal_id}`} className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-white/[0.04]">
                <span className="text-[10px] text-zinc-600 shrink-0 w-24">{fmtDate(e.at)}</span>
                <span className="text-zinc-300 truncate">
                  {e.type === "phase_transition" ? `Phase ${e.payload?.from} → ${e.payload?.to}` : EVENT_LABELS[e.type] || e.type}
                  {e.payload?.title && <span className="text-zinc-500"> — {String(e.payload.title).slice(0, 48)}</span>}
                </span>
                <span className="text-[10px] text-zinc-600 truncate">{e.by_email}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="noam-card p-4" data-testid="cockpit-negos">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
            <Scale className="w-3.5 h-3.5 text-[#1D9E75]" /> Négociations actives
          </div>
          {negotiations.length === 0 && <div className="text-xs text-zinc-600 py-4 text-center">Aucune négociation.</div>}
          <div className="space-y-1.5">
            {negotiations.map((n) => (
              <Link key={n.id} to={n.deal_ref ? `/app/deals/${n.deal_ref}` : "/app/negociation"}
                className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-white/[0.04]">
                <Scale className="w-3 h-3 text-zinc-600 shrink-0" />
                <span className="text-zinc-300 truncate flex-1">{n.title || "Négociation"}</span>
                {n.deal_ref ? <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#1D9E75]/40 text-[#1D9E75] shrink-0">liée deal</span>
                  : <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/10 text-zinc-500 shrink-0">non liée</span>}
                <ArrowUpRight className="w-3 h-3 text-zinc-600 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
